import { Menu } from '@headlessui/react';
import { useQueryClient } from '@tanstack/react-query';
import cns from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { QUERY_KEY_ME } from '@zep/apis';
import { useAuth } from '@zep/auth';
import { openZEMChargingPage } from '@zep/common/routes';
import { Button } from '@zep/ui';
import { LoginUserMenuList } from '../../../consts';
import { ZemColorIcon } from '../../icons';
import S from './Header.module.scss';
const ZEM_BALANCE_DEFAULT_TEXT = {
  ko: {
    total: '총 보유 {{zem}}ZEM',
    receivedGift: '(선물받은 {{zem}}ZEM)'
  },
  en: {
    total: 'Total {{zem}}ZEM',
    receivedGift: '(Gifted {{zem}}ZEM)'
  },
  ja: {
    total: '合計 {{zem}}ZEM',
    receivedGift: '(受け取った {{zem}}ZEM)'
  }
} as const;

/**
 * @returns 사용자 메뉴 컴포넌트
 */
const UserMenu = () => {
  const {
    user,
    logout
  } = useAuth();
  const queryClient = useQueryClient();
  const router = useRouter();
  const {
    t
  } = useTranslation(['layout'], {
    useSuspense: true
  });
  const {
    locale
  } = router;
  const ownedZem = user?.totalCashMoney ?? 0;
  const giftedZem = user?.giftCashMoney ?? 0;
  const totalZem = ownedZem + giftedZem;
  const zemBalanceDefaultText = ZEM_BALANCE_DEFAULT_TEXT[locale as keyof typeof ZEM_BALANCE_DEFAULT_TEXT] ?? ZEM_BALANCE_DEFAULT_TEXT.ko;
  const handleLogout = () => {
    void router.push('/home/landing').then(logout);
  };
  const toggleUserMenu = (isOpen: boolean) => {
    if (isOpen) {
      void queryClient.invalidateQueries(QUERY_KEY_ME);
    }
  };
  return <>
      <Menu as="div" className={cns(S.login_user_menu, S.header_menu)} data-sentry-element="Menu" data-sentry-source-file="UserMenu.tsx">
        {({
        open,
        close: closeUserMenu
      }) => <>
            <Menu.Button id="memberMenu" onClick={() => toggleUserMenu(!open)}>
              <span>{user?.name}</span>
              <Image width={16} height={16} src={`/images/light/common/caret-${open ? 'up' : 'down'}.svg`} alt="dropdown caret" />
            </Menu.Button>
            <Menu.Items as="div" className={cns(S.dropdown_menu_end, S.menu_items)}>
              <div className={S.menu_top}>
                <span>{user?.snsId}</span>
                <div className={S.zem_menu}>
                  <div className={S.zem_balance_list}>
                    <span className={S.current_zem}>
                      <ZemColorIcon width={20} height={20} />
                      <span className={S.zem_balance_text}>
                        <span>
                          {t('layout:header_total_zem_balance', {
                        zem: totalZem.toLocaleString(),
                        defaultValue: zemBalanceDefaultText.total
                      })}
                        </span>
                        <span className={S.gift_zem}>
                          {t('layout:header_received_gift_zem_balance', {
                        zem: giftedZem.toLocaleString(),
                        defaultValue: zemBalanceDefaultText.receivedGift
                      })}
                        </span>
                      </span>
                    </span>
                  </div>
                  <Button as={'a'} variant="secondary" size="xs" target="_blank" className={S.charge_zem_link} onClick={() => {
                openZEMChargingPage();
                closeUserMenu();
              }}>
                    {t('layout:header_zem_charge')}
                  </Button>
                </div>
              </div>

              <span className={S.contour_line} />

              <div className={S.menu_middle}>
                {LoginUserMenuList.filter(users => !users.imageResource && !users.hidden).map(userMenus => <Menu.Item as="span" key={userMenus.path}>
                    {({
                active
              }) => <Link href={(userMenus.useBaseURL && process.env.NEXT_PUBLIC_BASE_URL || '') + (locale === 'ko' ? userMenus.path : userMenus.pathEn ?? userMenus.path)} className={cns({
                [S.menu_hover]: active
              })} onClick={closeUserMenu} prefetch={false}>
                        {userMenus.imageResource && <Image width={18} height={18} src={`/images/${userMenus.imageResource}.svg`} alt="menu icon" />}
                        {t(userMenus.name)}
                      </Link>}
                  </Menu.Item>)}
              </div>

              <span className={S.contour_line} />

              <div className={S.menu_middle}>
                {LoginUserMenuList.filter(users => users.imageResource && !users.hidden).map(userMenus => <Menu.Item as="span" key={userMenus.path}>
                    {({
                active
              }) => <Link href={(userMenus.useBaseURL && process.env.NEXT_PUBLIC_BASE_URL || '') + (locale === 'ko' ? userMenus.path : userMenus.pathEn ?? userMenus.path)} className={cns({
                [S.menu_hover]: active
              })} onClick={closeUserMenu} prefetch={false}>
                        {userMenus.imageResource && <Image width={18} height={18} src={`/images/${userMenus.imageResource}.svg`} alt="favorite" />}
                        {t(userMenus.name)}
                      </Link>}
                  </Menu.Item>)}
              </div>
              <span className={S.contour_line} />
              <div className={S.menu_middle}>
                <Menu.Item as="span">
                  {({
                active
              }) => <button type="button" className={cns(S.user_menu_button, {
                [S.menu_hover]: active
              })} onClick={handleLogout}>
                      <Image width={18} height={18} src="/images/light/layout/logout-black.svg" alt="logout" />
                      {t('layout:header_layout_signout')}
                    </button>}
                </Menu.Item>
              </div>
            </Menu.Items>
          </>}
      </Menu>
    </>;
};
export default UserMenu;